import { FileFolderNameExtensions } from "./FileFolderNameExtensions";
import { getLastSplittedStringItem, removeExtension } from "../components/helpers/stringHelper";
import { IFileToUpload } from "../interfaces/IFileToUpload";
import { IOfficeService } from "../interfaces/IOfficeService";
import { OfficeServiceEnum } from "../components/enums/OfficeService";
import { IMailSendPayload } from "../interfaces/IMailSendPayload";
import { ModeEnum } from "../components/enums/modeEnum";
import { logger } from "../../../utilities/logger";
import { OutlookUtils } from "../../../utilities/OutlookUtils";
import { apiGetAttachmentExtension } from "./ApiService";

export class OutlookService implements IOfficeService {
  serviceType: OfficeServiceEnum;
  constructor() {
    this.serviceType = OfficeServiceEnum.outlook;
  }

  static getItemRestId = (itemId: string) => {
    if (itemId) {
      try {
        const hostName: string = Office.context?.mailbox?.diagnostics?.hostName;
        if (hostName === "OutlookIOS" || hostName === "OutlookAndroid") {
          return itemId;
        }
        else {
          return Office.context?.mailbox?.convertToRestId(itemId, Office.MailboxEnums.RestVersion.v2_0);
        }
      } catch (error) {
        logger(error, "OutlookService.ts (getItemRestId)");
      }
    }
    return null;
  };

  static getItemEWSId = (itemId: string) => {
    if (itemId) {
      try {
        const hostName: string = Office.context?.mailbox?.diagnostics?.hostName;
        if (hostName === "OutlookIOS" || hostName === "OutlookAndroid") {
          return null;
        }
        else {
          return itemId;
        }
      } catch (error) {
        logger(error, "OutlookService.ts (getItemEWSId)");
      }
    }
    return null;
  };

  getSubject = async (mode: ModeEnum, mailSendPayload: IMailSendPayload) => {
    let subject: string;
    if (mode === ModeEnum.mailSend) {
      subject = mailSendPayload.emailItem.name;
    } else {
      const { normalizedSubject } = Office.context?.mailbox?.item;
      subject = normalizedSubject;
    }

    return subject;
  };

  getItems = async (autoSelectAttachments?: boolean): Promise<IFileToUpload[]> => {
    const { itemId, normalizedSubject, attachments } = Office.context?.mailbox?.item;
    if (!itemId) return [];

    let itemAttachments = attachments?.length
      ? attachments
        .filter(attachment => attachment?.id && !attachment.isInline && attachment.size > 0)
        .map(attachment => {
          return {
            id: OutlookService.getItemRestId(attachment.id),
            ewsId: OutlookService.getItemEWSId(attachment.id),
            name: removeExtension(FileFolderNameExtensions.EnsureValidName(attachment.name)),
            extension: this.getExtension(attachment),
            isSelected: autoSelectAttachments === true,
            isAttachment: true,
            isDuplicate: false,
            contentType: attachment.contentType
          };
        })
        .filter(attachment => attachment?.id)
      : [];

    const attachmentsWithEmptyExtension = itemAttachments.filter(a => a.ewsId && !a.extension && a.contentType && a.contentType !== "application/octet-stream");
    if (attachmentsWithEmptyExtension?.length > 0) {
      const ewsUrl: string = OutlookUtils.getEWSURL();
      const ewsToken: string = await OutlookUtils.getEWSToken();

      if (ewsUrl && ewsToken) {
        const extensionData = await this.requestExtensions(ewsUrl, ewsToken, attachmentsWithEmptyExtension.map(a => a.ewsId));
        if (Array.isArray(extensionData)) {
          for (let i = 0; i < extensionData.length; i++) {
            const extData = extensionData[i];
            const attachment = itemAttachments.find(a => a.ewsId === extData.id);
            if (attachment) {
              attachment.extension = extData.extension;
            }
          }
        }
      }
    }

    let mainMailItem = {
      id: OutlookService.getItemRestId(itemId),
      ewsId: OutlookService.getItemEWSId(itemId),
      name: FileFolderNameExtensions.EnsureValidName(normalizedSubject),
      extension: "eml",
      isSelected: autoSelectAttachments !== true || (autoSelectAttachments === true && !itemAttachments?.length),
      isAttachment: false,
      isDuplicate: false,
      isMainMailItem: true
    };

    return [mainMailItem, ...itemAttachments];
  };

  private getExtension = (attachment: Office.AttachmentDetails) => {
    try {
      if (attachment.name?.includes(".")) {
        return getLastSplittedStringItem(attachment.name, /[\s.]+/);
      }
      else if (attachment.contentType === "message/rfc822") {
        return "eml"
      }
    } catch (error) {
      logger(error, "OutlookService.ts (getExtension)", attachment?.name);
    }
    return "";
  }

  private requestExtensions = async (ewsUrl: string, ewsToken: string, attachmentIdList: string[]) => {
    try {
      return await apiGetAttachmentExtension(ewsUrl, ewsToken, attachmentIdList);
    } catch (e) {
      logger(e, "OutlookService.ts (requestExtensions)");
    }
    return null;
  }
}
